import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { GoogleHealthIndicator } from './GoogleHealthIndicator';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { 
  LayoutDashboard, 
  Building2, 
  Users, 
  Mail, 
  FileText, 
  FolderOpen, 
  Settings,
  LogOut,
  Crown,
  ChevronLeft,
  Menu,
  DollarSign,
  Terminal,
  Calendar,
  Database,
  Download,
  Cloud,
  AlertTriangle,
  Bot,
  Sparkles
} from 'lucide-react';
import { cn } from '../lib/utils';
import { AIAssistant } from './AIAssistant';
import { authFetch } from '../lib/api';

const navItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/calendar', icon: Calendar, label: 'Calendar' },
  { path: '/organizations', icon: Building2, label: 'Organisations' },
  { path: '/export-data', icon: Database, label: 'View Data' },
  { path: '/workflows', icon: FolderOpen, label: 'Workflows' },
  { path: '/email-hub', icon: Mail, label: 'Email Hub' },
  { path: '/automation', icon: Bot, label: 'Automation', featureFlag: 'automation_assistant_enabled' },
  { path: '/assist-me', icon: Sparkles, label: 'Assist Me', featureFlag: 'assist_me_enabled' },
  { path: '/resources', icon: FolderOpen, label: 'Resources' },
  { path: '/check-orders', icon: FileText, label: 'Check Orders' },
  { path: '/billing', icon: DollarSign, label: 'AI Billing', adminOnly: true },
  { path: '/godmode', icon: Terminal, label: 'God Mode', adminOnly: true, danger: true },
  { path: '/settings', icon: Settings, label: 'Settings' },
];

export const MainLayout = () => {
  const { user, logout, googleNeedsConnect, setGoogleNeedsConnect } = useAuth();
  const { theme, toggleTheme, currentThemeName, getNextThemeName, isBarbiePink } = useTheme();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sydneyTime, setSydneyTime] = useState('');
  const [showGoogleModal, setShowGoogleModal] = useState(false);
  const [connectingGoogle, setConnectingGoogle] = useState(false);
  const [featureFlags, setFeatureFlags] = useState({});

  // Pull the current set of feature flags so we know which beta menu items
  // (Automation, Assist Me) to render. Non-admins also receive this — the
  // backend already filters admin_only flags down to `enabled: false` for
  // them, so the same filter logic Just Works.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const apiBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
        const res = await authFetch(`${apiBase}/api/feature-flags`);
        if (res.ok && !cancelled) {
          const data = await res.json();
          setFeatureFlags(data.flags || {});
        }
      } catch {
        /* non-fatal — menu falls back to hiding flagged items */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const visibleNavItems = navItems.filter(item => {
    if (item.adminOnly && user?.role !== 'admin') return false;
    // Admin always sees featureFlag-gated items — the flag controls
    // visibility to non-admins only. That way admins can build + validate
    // a feature before releasing it to the team. Non-admins are gated by
    // the actual flag state.
    if (item.featureFlag) {
      if (user?.role === 'admin') return true;
      if (!featureFlags[item.featureFlag]?.enabled) return false;
    }
    return true;
  });

  const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

  // Subtle notification when Google services aren't connected. Toast is
  // shown once per session (until dismissed) so the user is informed
  // without an aggressive full-screen modal blocking the workflow. The
  // heartbeat indicator on the avatar carries the status the rest of the
  // time. Click the toast action OR the avatar dropdown to (re-)connect.
  useEffect(() => {
    if (!googleNeedsConnect) return;
    const toastId = toast.warning('Google services not connected', {
      id: 'google-not-connected',  // dedupe — one toast at a time
      description: 'Email send and document generation may fail until you reconnect.',
      duration: 8000,
      action: {
        label: 'Reconnect',
        onClick: () => setShowGoogleModal(true),
      },
    });
    return () => toast.dismiss(toastId);
  }, [googleNeedsConnect]);

  // Connect to Google services
  const handleConnectGoogle = async () => {
    setConnectingGoogle(true);
    try {
      const response = await authFetch(`${API}/api/gmail/connect`);
      if (response.ok) {
        const data = await response.json();
        if (data.authorization_url) {
          window.location.href = data.authorization_url;
        }
      }
    } catch (err) {
      console.error('Failed to initiate Google connection:', err);
    } finally {
      setConnectingGoogle(false);
    }
  };

  // Dismiss the modal for now
  const handleDismissGoogle = () => {
    setShowGoogleModal(false);
    setGoogleNeedsConnect(false);
  };

  // Update Sydney time every second
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const sydneyFormatter = new Intl.DateTimeFormat('en-AU', {
        timeZone: 'Australia/Sydney',
        day: '2-digit',
        month: 'short',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
      setSydneyTime(sydneyFormatter.format(now));
    };
    
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside 
        className={cn(
          "fixed left-0 top-0 h-full z-40 transition-all duration-300 border-r border-border",
          sidebarCollapsed ? "w-20" : "w-64",
          isBarbiePink ? "bg-card" : "bg-white/70 backdrop-blur-xl",
          "hidden lg:flex flex-col"
        )}
      >
        {/* Logo and Hamburger */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-border">
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className={cn(
              "flex items-center gap-3 transition-all",
              sidebarCollapsed && "justify-center"
            )}
            title={sidebarCollapsed ? "Expand menu" : "Collapse menu"}
          >
            <div className={cn(
              "p-2 rounded-lg",
              isBarbiePink ? "bg-pink-500/20" : "bg-primary/10"
            )}>
              <Menu className="w-6 h-6 text-primary" />
            </div>
            {!sidebarCollapsed && (
              <div>
                <span className="font-bold text-lg tracking-tight">G.S Workflow</span>
              </div>
            )}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 px-2 overflow-y-auto">
          <ul className="space-y-0.5">
            {visibleNavItems.map((item) => (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  className={({ isActive }) => cn(
                    "flex items-center gap-2.5 px-2.5 py-2 rounded-md transition-all text-sm",
                    sidebarCollapsed && "justify-center px-2",
                    isActive 
                      ? item.danger 
                        ? "bg-red-600 text-white"
                        : "bg-primary text-primary-foreground" 
                      : item.danger
                        ? "text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 hover:text-red-600"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  )}
                >
                  <item.icon className="w-4 h-4 shrink-0" />
                  {!sidebarCollapsed && <span>{item.label}</span>}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* Collapse button */}
        <div className="p-3 border-t border-border">
          <Button
            data-testid="sidebar-collapse-btn"
            variant="ghost"
            size="sm"
            className="w-full justify-center"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          >
            <ChevronLeft className={cn(
              "w-5 h-5 transition-transform",
              sidebarCollapsed && "rotate-180"
            )} />
          </Button>
        </div>
      </aside>

      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile sidebar */}
      <aside className={cn(
        "fixed left-0 top-0 h-full w-64 z-40 border-r border-border transition-transform lg:hidden",
        isBarbiePink ? "bg-card" : "bg-white",
        mobileMenuOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="h-16 flex items-center px-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className={cn(
              "p-2 rounded-lg",
              isBarbiePink ? "bg-pink-500/20" : "bg-primary/10"
            )}>
              <Menu className="w-6 h-6 text-primary" />
            </div>
            <span className="font-bold text-lg tracking-tight">G.S Workflow</span>
          </div>
        </div>
        <nav className="py-6 px-3">
          <ul className="space-y-1">
            {visibleNavItems.map((item) => (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) => cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all",
                    isActive 
                      ? item.danger 
                        ? "bg-red-600 text-white"
                        : "bg-primary text-primary-foreground" 
                      : item.danger
                        ? "text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 hover:text-red-600"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  )}
                >
                  <item.icon className="w-5 h-5 shrink-0" />
                  <span className="font-medium">{item.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      </aside>

      {/* Main content */}
      <main className={cn(
        "flex-1 transition-all duration-300",
        sidebarCollapsed ? "lg:ml-20" : "lg:ml-64"
      )}>
        {/* Top bar */}
        <header className="h-16 border-b border-border flex items-center justify-between px-4 lg:px-8 sticky top-0 bg-background/95 backdrop-blur z-20">
          <div className="flex items-center gap-4">
            {/* Mobile hamburger only */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setMobileMenuOpen(true)}
            >
              <Menu className="w-5 h-5" />
            </Button>
          </div>

          <div className="flex items-center gap-4">
            {/* Theme toggle */}
            <Button
              data-testid="header-theme-toggle"
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="relative"
              title={`Current: ${currentThemeName} | Click for: ${getNextThemeName()}`}
            >
              <Crown className="w-5 h-5 text-primary" />
            </Button>

            {/* User menu with Sydney time */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  data-testid="user-menu-trigger"
                  variant="ghost" 
                  className="flex items-center gap-3 px-2"
                >
                  <GoogleHealthIndicator connected={!googleNeedsConnect}>
                    <Avatar className="w-8 h-8">
                      <AvatarImage src={user?.picture} alt={user?.name} />
                      <AvatarFallback>{getInitials(user?.name)}</AvatarFallback>
                    </Avatar>
                  </GoogleHealthIndicator>
                  <div className="hidden sm:flex flex-col items-start">
                    <span className="font-medium text-sm">{user?.name}</span>
                    <span className="text-[10px] text-muted-foreground">{sydneyTime} Sydney</span>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="font-medium">{user?.name}</p>
                  <p className="text-sm text-muted-foreground">{user?.email}</p>
                  <p className={`mt-1 text-[11px] flex items-center gap-1 ${googleNeedsConnect ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${googleNeedsConnect ? 'bg-rose-500' : 'bg-emerald-500'}`} />
                    {googleNeedsConnect ? 'Google not connected' : 'Google connected'}
                  </p>
                </div>
                <DropdownMenuSeparator />
                {googleNeedsConnect && (
                  <DropdownMenuItem
                    data-testid="reconnect-google-btn"
                    onClick={() => setShowGoogleModal(true)}
                    className="text-rose-600 focus:text-rose-700 dark:text-rose-400"
                  >
                    <Cloud className="w-4 h-4 mr-2" />
                    Reconnect Google services
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem 
                  data-testid="logout-btn"
                  onClick={handleLogout}
                  className="text-destructive focus:text-destructive"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <div className="p-4 lg:p-8">
          <Outlet />
        </div>
      </main>
      
      {/* AI Assistant - Global */}
      <AIAssistant />

      {/* Google Services Connection Modal */}
      <Dialog open={showGoogleModal} onOpenChange={setShowGoogleModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Cloud className="w-5 h-5 text-blue-500" />
              Connect Google Services
            </DialogTitle>
            <DialogDescription>
              To use all features of G.S Workflow (Gmail, Google Drive, Document Templates), you need to connect your Google account.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-start gap-3 p-3 bg-amber-50 dark:bg-amber-950 rounded-lg border border-amber-200 dark:border-amber-800">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-amber-800 dark:text-amber-200">Connection Required</p>
                <p className="text-amber-700 dark:text-amber-300">
                  Without Google services, you won't be able to send emails, edit document templates, or use cloud storage features.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={handleDismissGoogle}>
                Remind Me Later
              </Button>
              <Button onClick={handleConnectGoogle} disabled={connectingGoogle}>
                {connectingGoogle ? (
                  <>Connecting...</>
                ) : (
                  <>
                    <Cloud className="w-4 h-4 mr-2" />
                    Connect Google
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
